import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import productService from '../../../services/productService';
import { Plus, Search, Trash2, Edit, RefreshCcw, Archive, RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import ProductModal from './components/ProductModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import SyncLoadingOverlay from '../../../components/SyncLoadingOverlay';

const Products = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [totalProducts, setTotalProducts] = useState(0);
  
  // Check if user is admin, redirect if not
  useEffect(() => {
    if (user?.role !== 'admin' && !user?.isAdmin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Define fetchProducts with useCallback to prevent unnecessary re-creation
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const offset = page * limit;
      const response = await productService.getAllProducts({ 
        limit, 
        offset,
        include_archived: showArchived 
      });

      console.log(response);
      
      if (response.success) {
        setProducts(response.products);
        setTotalProducts(response.pagination?.total || 0);
      } else {
        setError(response.message || 'Failed to fetch products');
        setProducts([]);
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, showArchived]);

  // Load products on initial render
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSyncWithPolar = useCallback(async () => {
    try {
      setSyncing(true);
      const response = await productService.syncWithPolar();
      
      if (response.success) {
        // Refresh products list after sync
        await fetchProducts();
      } else {
        setError(response.message || 'Sync failed');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during sync');
    } finally {
      setSyncing(false);
    }
  }, [fetchProducts]);

  const handleCreateProduct = () => {
    setCurrentProduct(null);
    setModalOpen(true);
  };

  const handleEditProduct = (product) => {
    setCurrentProduct(product);
    setModalOpen(true);
  };

  const handleDeleteClick = (product) => {
    setCurrentProduct(product);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!currentProduct) return;
    
    try {
      setLoading(true);
      const response = await productService.deleteProduct(currentProduct.id);
      if (response.success) {
        // Remove product from list or refresh the list
        setProducts(products.filter(p => p.id !== currentProduct.id));
      } else {
        setError(response.message || 'Failed to delete product');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while deleting the product');
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
      setCurrentProduct(null);
    }
  };

  const handleArchiveProduct = async (product) => {
    try {
      setLoading(true);
      const response = await productService.archiveProduct(product.id);
      if (response.success) {
        // Update the product in the list
        setProducts(products.map(p => 
          p.id === product.id ? { ...p, is_archived: true } : p
        ));
      } else {
        setError(response.message || 'Failed to archive product');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while archiving the product');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreProduct = async (product) => {
    try {
      setLoading(true);
      const response = await productService.restoreProduct(product.id);
      if (response.success) {
        // Update the product in the list
        setProducts(products.map(p => 
          p.id === product.id ? { ...p, is_archived: false } : p
        ));
      } else {
        setError(response.message || 'Failed to restore product');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while restoring the product');
    } finally {
      setLoading(false);
    }
  };

  const handleProductSave = async (productData) => {
    try {
      setLoading(true);
      let response;
      
      if (currentProduct) {
        // Update existing product
        response = await productService.updateProduct(currentProduct.id, productData);
      } else {
        // Create new product
        response = await productService.createProduct(productData);
      }
      
      if (response.success) {
        // Refresh products list
        await fetchProducts();
      } else {
        setError(response.message || 'Operation failed');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
      setModalOpen(false);
    }
  };

  // Filter products based on search term
  const filteredProducts = products.filter(product => 
    product.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    product.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate total pages
  const totalPages = Math.ceil(totalProducts / limit);

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <h1 className="text-2xl font-bold mb-4 md:mb-0">Products Management</h1>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleSyncWithPolar}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
          >
            <RefreshCcw className="mr-2 h-4 w-4" />
            Sync with Polar.sh
          </button>
          <button
            onClick={handleCreateProduct}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Product
          </button>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search products..."
              className="w-full pl-10 pr-4 py-2 border rounded"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          <div className="flex items-center">
            <input
              id="showArchived"
              type="checkbox"
              checked={showArchived}
              onChange={() => setShowArchived(!showArchived)}
              className="mr-2"
            />
            <label htmlFor="showArchived">Show archived products</label>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          {error}
          <button
            onClick={() => setError(null)}
            className="absolute top-0 right-0 px-4 py-3"
          >
            <span className="sr-only">Close</span>
            &times;
          </button>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Price
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Support Credits
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Custom Credits
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Updated
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center">
                  <div className="flex justify-center">
                    <svg className="animate-spin h-5 w-5 text-gray-500" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                </td>
              </tr>
            ) : filteredProducts.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center">
                  No products found
                </td>
              </tr>
            ) : (
              filteredProducts.map((product) => (
                <tr key={product.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{product.name}</div>
                    <div className="text-sm text-gray-500 truncate max-w-xs">{product.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {product.is_recurring ? 'Subscription' : 'One-time'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {product.is_recurring ? product.recurring_interval || 'month' : ''}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      ${(() => {
                        let metadata = product.metadata;
                        
                        // Parse metadata if it's a string
                        if (metadata && typeof metadata === 'string') {
                          try {
                            metadata = JSON.parse(metadata);
                          } catch (e) {
                            console.error('Failed to parse product metadata:', e);
                          }
                        }
                        
                        // Safely access price_amount
                        const priceAmount = metadata?.price_amount;
                        return priceAmount ? (priceAmount / 100).toFixed(2) : '0.00';
                      })()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {product.support_credits || 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {product.custom_credits || 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      product.is_archived 
                        ? 'bg-gray-100 text-gray-800' 
                        : product.active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {product.is_archived 
                        ? 'Archived' 
                        : product.active 
                          ? 'Active' 
                          : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {product.updated_at 
                      ? formatDistanceToNow(new Date(product.updated_at), { addSuffix: true }) 
                      : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleEditProduct(product)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      
                      {product.is_archived ? (
                        <button
                          onClick={() => handleRestoreProduct(product)}
                          className="text-green-600 hover:text-green-900"
                          title="Restore"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleArchiveProduct(product)}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Archive"
                        >
                          <Archive className="h-4 w-4" />
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleDeleteClick(product)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center">
          <span className="text-sm text-gray-700 mr-4">
            Showing{' '}
            <span className="font-medium">
              {filteredProducts.length > 0 ? Math.min(page * limit + 1, totalProducts || 0) : 0}
            </span>
            {' '}to{' '}
            <span className="font-medium">
              {Math.min((page + 1) * limit, totalProducts || 0)}
            </span>
            {' '}of{' '}
            <span className="font-medium">{totalProducts || 0}</span>
            {' '}results
          </span>
          
          <div className="flex items-center">
            <label htmlFor="itemsPerPage" className="text-sm text-gray-700 mr-2">
              Items per page:
            </label>
            <select
              id="itemsPerPage"
              value={limit}
              onChange={(e) => {
                const newLimit = parseInt(e.target.value, 10);
                setLimit(newLimit);
                setPage(0); // Reset to first page when changing limit
              }}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={() => setPage(prev => Math.max(prev - 1, 0))}
            disabled={page === 0}
            className={`px-3 py-1 rounded ${
              page === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-blue-600 hover:bg-blue-50'
            }`}
          >
            Previous
          </button>
          <button
            onClick={() => setPage(prev => (prev + 1 < totalPages ? prev + 1 : prev))}
            disabled={page + 1 >= totalPages}
            className={`px-3 py-1 rounded ${
              page + 1 >= totalPages
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-blue-600 hover:bg-blue-50'
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* Product modal */}
      {modalOpen && (
        <ProductModal
          product={currentProduct}
          onClose={() => setModalOpen(false)}
          onSave={handleProductSave}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Product"
          message={`Are you sure you want to delete the product "${currentProduct?.name}"? This action cannot be undone.`}
        />
      )}

      {/* Syncing overlay */}
      {syncing && (
        <SyncLoadingOverlay message="Syncing products with Polar.sh..." />
      )}
    </div>
  );
};

export default Products;
